import Alert from '@/components/Alert'
import buildScreenHeaderItems from '@/components/buildScreenHeaderItems'
import UniversalWebView from '@/components/UniversalWebView'
import { isBelowiOS26 } from '@/lib/utils/iosCompat'
import {
  createTemplateUsageThunk,
  fetchTemplateDetailThunk,
  resetUseTemplateState,
  templateSelectors,
} from '@/redux/slices/templateSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { UseTemplateErrorType } from '@/typings/template'
import { Stack, useLocalSearchParams, useRouter } from 'expo-router'
import { useIsFocused } from 'expo-router/react-navigation'
import { get } from 'lodash-es'
import { useCallback, useEffect, useMemo } from 'react'
import { Platform, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

export default function TemplateDetail() {
  const insets = useSafeAreaInsets()
  const router = useRouter()
  const dispatch = useAppDispatch()
  const isFocused = useIsFocused()
  const { templateToken } = useLocalSearchParams<{ templateToken: string }>()

  const template = useAppSelector(state => (templateToken ? templateSelectors.selectById(state, templateToken) : null))
  const { isLoading: isUsingTemplate, result, error } = useAppSelector(state => state.templates.templateUsageState)

  const uri = useMemo(() => {
    return `${process.env.EXPO_PUBLIC_NEXT_BASE_URL}/templates/${templateToken}?frm=index`
  }, [templateToken])

  useEffect(() => {
    if (!templateToken) return

    dispatch(resetUseTemplateState())
    dispatch(fetchTemplateDetailThunk({ templateToken }))

    return () => {
      dispatch(resetUseTemplateState())
    }
  }, [templateToken, dispatch])

  const handleMessage = useCallback(
    (data: { event: string; details: unknown }) => {
      if (data.event === 'TEMPLATE_TAG_NAVIGATION') {
        const tag = get(data.details, 'tag', '')
        router.push(`/templates?tag=${tag}`)
      } else if (data.event === 'TEMPLATE_NAVIGATION') {
        const token = get(data.details, 'templateToken', '')
        if (token) router.push(`/templates/${token}`)
      }
    },
    [router]
  )

  useEffect(() => {
    // templateUsageState is shared: stacked TemplateDetail screens all observe it,
    // so only the focused one may alert
    if (!isFocused) return

    if (result?.form_token) {
      Alert.alert('成功', '表单创建成功！', [
        {
          text: '查看表单',
          onPress: () => {
            dispatch(resetUseTemplateState())
            router.replace(`/(system)/forms/${result.form_token}/edit`)
          },
        },
      ])
    } else if (error) {
      if (error.error === UseTemplateErrorType.TEMPLATE_PURCHASE_REQUIRED) {
        Alert.alert('付费模板', '此模板需购买后才能选用，请移步电脑端进行购买')
      } else if (error.error === UseTemplateErrorType.PAID_FEATURE_REQUIRED) {
        Alert.alert('套餐不支持', '当前套餐暂不支持选用此模板，请升级套餐之后再选用')
      } else {
        Alert.alert('套餐不支持', '当前套餐暂不支持选用此模板')
      }
      dispatch(resetUseTemplateState())
    }
  }, [isFocused, result, error, router, dispatch])

  const handleUseTemplate = useCallback(() => {
    if (!template || !templateToken) return

    dispatch(
      createTemplateUsageThunk({
        templateToken,
        scene: template.gdScene,
        tags: template.tags.map(tag => tag.name),
        frm: 'index',
      })
    )
  }, [template, templateToken, dispatch])

  return (
    <View className="flex-1" style={Platform.OS === 'android' ? { paddingBottom: insets.bottom } : undefined}>
      <Stack.Screen
        options={{
          title: '模板详情',
          headerTransparent: Platform.OS === 'ios' && !isBelowiOS26,
          ...buildScreenHeaderItems({
            right: !template
              ? []
              : [
                  {
                    type: 'button',
                    label: isUsingTemplate ? '选用中...' : '选用模板',
                    onPress: handleUseTemplate,
                    disabled: isUsingTemplate,
                    variant: 'prominent',
                  },
                ],
          }),
        }}
      />
      <UniversalWebView source={{ uri }} className="flex-1 bg-background-muted" onMessage={handleMessage} />
    </View>
  )
}
